import { mergeWith } from 'lodash';
import type { OasTableColumn } from '../../oas';

export type ColumnFilterSupport = {
  // List of operators that can be used to compare this column to a value
  operators: string[];
  // List of operators that can be used to compare this column to another column
  crossColumnOperators: string[];
  // List of column names this column can be compared to.
  // Empty list - cross-column operation is not supported at all
  comparableColumns: string[];
  // Defined only when the column is an object.
  // Each object property has it's own ColumnFilterSupport definition
  objectProperties?: { [nestedObjectFieldKey: string]: ColumnFilterSupport };
  // Defined only when the column is an array.
  // Contains ColumnFilterSupport definition for the array item
  arrayItem?: ColumnFilterSupport;
};

type TableFilterSupport = {
  [columnKey: string]: ColumnFilterSupport;
};

type FilterItem = {
  enum?: string[];
};

type FilterAlternative = {
  /**
   * @description used for tuple validation in OAS 3.1
   * @see https://json-schema.org/understanding-json-schema/reference/array#tupleValidation
   * support added to our internal fork of `zod-to-json-schema` in this commit
   * https://gitlab.com/ip-fabric/development/product/zod-to-json-schema/-/commit/4f2810fa52858a25b15ba7c8a4434524a9a200c8
   * also illustrated in https://gitlab.com/ip-fabric/development/product/zod-to-json-schema/-/blob/5f9168961c1b20d8b4112bf3f4b18725bd76f427/dist/src/parsers/tuple.d.ts#L7-8
   *
   */
  prefixItems: FilterItem[];
};

type ColumnFilterDefinition = {
  anyOf: FilterAlternative[];
};

export type OasFilters = {
  properties: {
    [columnKey: string]: ColumnFilterDefinition;
  };
};

const emptyFilterSupport = (): ColumnFilterSupport => ({
  operators: [],
  crossColumnOperators: [],
  comparableColumns: [],
});

const nestArrayItem = (
  arrayItem: ColumnFilterSupport,
): ColumnFilterSupport => ({
  ...emptyFilterSupport(),
  arrayItem,
});

const nestObjectProperty = (
  key: string,
  objectProperty: ColumnFilterSupport,
): ColumnFilterSupport => ({
  ...emptyFilterSupport(),
  objectProperties: {
    [key]: objectProperty,
  },
});

/**
 * Concatenates two arrays, removing duplicates
 */
const combineArrays = <T>(objValue: T, srcValue: T): T[] | undefined => {
  if (Array.isArray(objValue)) {
    return Array.from(new Set(objValue.concat(srcValue)));
  }

  return undefined;
};

const combineFilterSupports = (
  s1: ColumnFilterSupport,
  s2: ColumnFilterSupport,
): void => {
  mergeWith(s1, s2, combineArrays);
};

const getColumnFilterSupport = (
  definition: ColumnFilterDefinition,
): ColumnFilterSupport => {
  const filterAlternatives = <FilterAlternative[]>definition.anyOf;

  // TODO NIM-13945: Remove hard-coded 'color' from `operators`
  const combinedFilterSupport = {
    ...emptyFilterSupport(),
    operators: ['color'],
  };

  for (const filterAlternative of filterAlternatives) {
    let filterSupport = emptyFilterSupport();

    const prefixItems = (filterAlternative.prefixItems ?? []).filter(Boolean);
    if (prefixItems.length === 0) {
      continue;
    }

    // Right-anchored parsing: last => target, before-last => operator, then optional modifier(s)
    const reversed = prefixItems.toReversed();

    const target = reversed[0];
    const operator = reversed[1]; // may be undefined in 3.1 tuples!
    const modifier = reversed[2];
    const dynamic = reversed.slice(3);

    const operatorValue = getFilterItemValue(operator);
    const modifierValue = getFilterItemValue(modifier);

    // If there is no operator keyword, this is either a simple/no-operator form or malformed.
    if (!operatorValue) {
      continue;
    }

    if (!modifier) {
      // Simple value operation (e.g. "eq 123")
      // No need to process further

      filterSupport.operators.push(...operatorValue);
      combineFilterSupports(combinedFilterSupport, filterSupport);
      continue;
    }

    switch (modifierValue?.[0]) {
      case 'column':
        filterSupport.comparableColumns = target?.enum ?? [];
        filterSupport.crossColumnOperators.push(...operatorValue);
        break;
      case 'color':
        filterSupport.operators.push('color');
        break;
      case 'now':
        filterSupport.operators.push('now');
        break;
      default:
        // Not a modifier, but a dynamic value (e.g. "hostname eq 123" or "all eq 0")
        // Add it to the `dynamic` and process it
        dynamic.unshift(modifier);
        filterSupport.operators.push(...operatorValue);
    }

    filterSupport = applyDynamicAlternatives(dynamic, filterSupport);

    combineFilterSupports(combinedFilterSupport, filterSupport);
  }

  return combinedFilterSupport;
};

export const getTableFilterSupport = (
  oasFilters: OasFilters,
  visibleColumns: OasTableColumn[],
): TableFilterSupport => {
  const filterSupport: TableFilterSupport = {};

  const entries = Object.entries(oasFilters.properties)
    // remove advanced filter operators from the list
    .filter(([columnKey]) => columnKey !== 'and' && columnKey !== 'or')
    // filter out columns that are not visible in the table
    .filter(([columnKey]) => visibleColumns.some((x) => x.key === columnKey));

  for (const [columnKey, definition] of entries) {
    filterSupport[columnKey] = getColumnFilterSupport(definition);
  }

  return filterSupport;
};

const getFilterItemValue = (item?: FilterItem): string[] | undefined => {
  if (!item) {
    return undefined;
  }
  if (Array.isArray(item.enum) && item.enum.length) {
    return item.enum;
  }
  return undefined;
};

const applyDynamicAlternatives = (
  alternatives: FilterItem[],
  current: ColumnFilterSupport,
): ColumnFilterSupport => {
  let result = current;
  for (const alternative of alternatives) {
    const keyword = getFilterItemValue(alternative)?.[0];
    if (keyword === undefined) {
      throw new Error('Table column filter alternative is missing keyword');
    }
    if (['all', 'any'].includes(keyword)) {
      result = nestArrayItem(result);
    } else {
      result = nestObjectProperty(keyword, result);
    }
  }
  return result;
};
